import { captureError } from "@/lib/monitoring";

// Maps gender-identity codes to the identified-gender label shown in the PatientBar.
// Intentionally uses short labels (e.g. "Female") rather than the full Profile
// dropdown labels ("Transgender Female") — keep these separate.
const IDENTIFIED_GENDER_LABEL: Record<string, string> = {
  male: "Male",
  "transgender-male": "Male",
  female: "Female",
  "transgender-female": "Female",
  "non-binary": "Non-binary",
  other: "Other",
  "non-disclose": "Undisclosed",
};

export const formatPatientAgeGender = ({
  age,
  administrativeGender,
  genderIdentity,
  preferredPronouns,
  dob,
}: {
  age: number;
  administrativeGender: string;
  genderIdentity?: string | null;
  preferredPronouns?: string | null;
  dob?: string;
}) => {
  const baseText = `${age}${administrativeGender.charAt(0).toUpperCase()}`;

  // Only format DOB if it's valid and exists
  let formattedDob = "";
  if (dob) {
    try {
      // For consistent formatting in tests, parse the date more explicitly
      // Handles ISO format dates (YYYY-MM-DD) properly
      if (dob.includes("-")) {
        const [year, month, day] = dob.split("-");
        formattedDob = `${month}/${day}/${year}`;
      } else {
        // For other formats, use the Date object with timezone handling
        const date = new Date(dob);
        if (!Number.isNaN(date.getTime())) {
          formattedDob = date.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          });
        }
      }
    } catch (e) {
      // If date parsing fails, leave formattedDob as empty string
      captureError(
        new Error(`Error parsing date for Patient Demographics: ${dob} ${e}`),
      );
    }
  }

  // Only include gender identity if the mapped label differs from administrative gender.
  // e.g. transgender-female + admin female → "Female" matches "Female" → suppressed (redundant).
  //      transgender-female + admin male → "Female" vs "Male" → shown.
  const identifiedGenderLabel = genderIdentity
    ? IDENTIFIED_GENDER_LABEL[genderIdentity]
    : undefined;
  const showGenderIdentity =
    !!identifiedGenderLabel &&
    identifiedGenderLabel.toLowerCase() !== administrativeGender.toLowerCase();

  const additionalInfo = [
    showGenderIdentity && (`Identifies ${identifiedGenderLabel}` as string),
    preferredPronouns && preferredPronouns,
  ]
    .filter(Boolean)
    .join(", ");

  return additionalInfo
    ? formattedDob
      ? `${baseText} (${additionalInfo}) ${formattedDob}`
      : `${baseText} (${additionalInfo})`
    : formattedDob
      ? `${baseText} ${formattedDob}`
      : baseText;
};
